"use client";

import { useFormStatus } from "react-dom";

/**
 * A submit button that shows its own form's pending state, for the plain
 * `<form action={fn.bind(null, id)}>` submits this app renders from server
 * components (the status band on a group, the Approve / Decline / Undo on a
 * parked review, sign-in and the stranded sign-out). Same job as restock's
 * `PendingSubmit`; the focus handling is idreamofneon's `PendingButton`.
 *
 * `useFormStatus` reads the NEAREST ENCLOSING <form>, so this has to be rendered
 * inside the form it reports on, never beside it.
 *
 * While pending:
 * - `aria-disabled` + `aria-busy`, and a click that does nothing. NOT the
 *   `disabled` attribute: a disabled button leaves the tab order, so the
 *   browser drops focus to <body> the instant it is pressed and a keyboard
 *   user loses their place for the length of the round trip. `preventDefault`
 *   on the click is what stops the form going a second time.
 * - an inline spinner and `pendingLabel` in place of the label.
 *
 * The width does not move. `data-ghost` carries whichever label is NOT showing,
 * and `.pending-label::after` draws it invisibly in the same grid cell, so the
 * button is always as wide as the wider of the two. A pseudo-element rather
 * than a hidden span: it is in no one's textContent and, being
 * `visibility: hidden`, in no accessible name either.
 *
 * `pending`, when passed, overrides `useFormStatus` — for a form submitted from
 * `onSubmit` through its own `useTransition` rather than an `action`. That is
 * the shape an EDIT form needs: React resets an uncontrolled form once its
 * action settles, which after a refused save would put the stored values back
 * over the ones she just typed (settings' EditAppForm).
 */
export function PendingButton({
  className,
  children,
  pendingLabel,
  pending,
}: {
  className: string;
  children: string;
  pendingLabel: string;
  pending?: boolean;
}) {
  const status = useFormStatus();
  const busy = pending ?? status.pending;
  return (
    <button
      type="submit"
      className={className}
      aria-disabled={busy || undefined}
      aria-busy={busy || undefined}
      onClick={(e) => {
        if (busy) e.preventDefault();
      }}
    >
      <span
        className={busy ? "pending-label is-pending" : "pending-label"}
        data-ghost={busy ? children : pendingLabel}
      >
        {busy ? (
          <span className="pending-label-busy">
            <span className="busy-spinner" aria-hidden="true" />
            {pendingLabel}
          </span>
        ) : (
          <span>{children}</span>
        )}
      </span>
    </button>
  );
}
